import { AKMeta, ChatMessageItem, ChatMessageItemTypes, ChatMessageUpdate, ChatMessageUpdateTypes, ContentPart, ContentPartDelta, ErrorItem, TextMessageItem } from "@aidkitorg/types/lib/convoai/conversation";
import { ResponseAudioTranscriptDoneEvent, ResponseTextDoneEvent } from 'openai/resources/beta/realtime/realtime';
import React, { ReactNode, Reducer } from "react";
import { ChatEvent } from "../AICoach/ChatChannel";

type ChatContextType = {
  messages: ChatMessageItem[],
  sendMessage(message: ChatMessageUpdate): void
};

export const ChatContext = React.createContext<ChatContextType>({
  messages: [],
  sendMessage() { console.warn('no chat context setup, ignoring message'); }
});

export const ChatProvider: React.FC<{ context: ChatContextType, children: ReactNode }> = ({ context, children }) => {
  return <ChatContext.Provider value={context}>
    {children}
  </ChatContext.Provider>
};

type ChatState = {
  map: Map<string, number>,
  toolCalls: Set<number>,
  list: ChatMessageItem[],
  errors?: ErrorItem[]
};

export const AudioChatStateReducer: Reducer<ChatState, ChatEvent> = (state, action) => ChatMessageReducer(state, AudioChatAdapter(action));

/**
 * This reducer drives the chat state in its entirety (audio and text).
 */
export const ChatMessageReducer: Reducer<ChatState, ChatMessageUpdate | { type: 'clear-chat' }> = ({ map, list, toolCalls, errors }, action) => {

  function updateItemMeta(itemId: string, update: Partial<AKMeta>) {
    const itemIndex = map.get(itemId);
    if (typeof itemIndex === 'number') {
      const itemToUpdate = list[itemIndex];
      itemToUpdate.meta = {
        time: Date.now(),
        ...itemToUpdate.meta,
        ...update
      };
    }
  }

  function upsertItem<T extends ChatMessageUpdate, E = T['type'] extends ChatMessageItem['type'] ? ChatMessageItemTypes[T['type']] : ChatMessageItem>(
    id: string,
    item: T,
    merge: (item: E, update: T) => E): [number, E] {

    if (!map.has(id)) map.set(id, list.length);

    const index = map.get(id)!;

    const existing = list[index] ?? {};

    const updatedItem = merge(existing as Parameters<typeof merge>[0], item);

    (list[index] as E) = {
      id,
      ...updatedItem
    };

    return [index, list[index] as E];
  }

  function appendError(error: ErrorItem) {
    return (errors || []).concat(error);
  }

  switch (action.type) {

    case "clear-chat":
      return {
        map: new Map(),
        list: [],
        toolCalls: new Set()
      };

    case "update_meta":
      updateItemMeta(action.id, action.meta);
      break;

    case "content_delta":
      upsertItem<ChatMessageUpdateTypes['content_delta'], TextMessageItem>(action.id, action, (existing, update) => {

        existing.content[update.index] =
          update.overwrite ?
            update.content :
            { type: 'text', content: (existing.content?.[update.index]?.content || '') + update.content.content };

        return existing;
      });
      break;

    case "message":
      upsertItem(action.id, action, (existing, update) => {

        const content: ContentPart[] = [];
        const contentSize = Math.max(existing.content?.length || 0, update.content?.length || 0);
        for (let i = 0; i < contentSize; i++) {
          content[i] = {
            type: 'text',
            content: update.content?.[i]?.content || existing.content?.[i]?.content || ''
          };
        }

        return {
          ...existing,
          ...update,
          content,
          meta: {
            time: Date.now(),
            ...existing.meta,
            ...(update.meta ?? {})
          }
        };
      });
      break;
    case "tool_call":
      const [itemIndex, updated] = upsertItem(action.id, action, (existing, update) => {
        return {
          ...existing,
          ...update,
          name: update.name || existing.name,
          arguments: update.arguments || existing.arguments,
          callId: update.callId || existing.callId,
          meta: {
            time: Date.now(),
            ...existing.meta,
            ...(update.meta ?? {})
          }
        };
      });
      if (updated.name && updated.arguments && updated.meta?.phase !== 'done') {
        map.set(updated.callId, itemIndex);
        toolCalls.add(itemIndex);
        // prevents additional calls from happening for the same tool call, 
        // mostly for after restoring from a previous conversation.
        updateItemMeta(action.id, {
          phase: 'done'
        });
      }
      break;
    case "tool_call_output":
      upsertItem(action.id, action, (existing, update) => {
        return {
          ...existing,
          ...update,
          output: update.output || existing.output,
          callId: update.callId || existing.callId,
          meta: {
            time: Date.now(),
            ...existing.meta,
            ...(update.meta ?? {})
          }
        };
      });
      const index = map.get(action.callId);
      if (typeof index === 'number') {
        map.delete(action.callId);
        toolCalls.delete(index);
      }
      break;
    case "error":
      errors = appendError(action);
      break;
    case "remove_item": {
      const index = map.get(action.id);
      if (typeof index === 'number') {
        map.delete(action.id);
        list.splice(index, 1);
      }
      break;
    }
    case "update_item": {
      upsertItem(action.id, action.with, (_, update) => update);
      break;
    }
  }

  return {
    map: new Map(map),
    list: Array.from(list),
    toolCalls: new Set(toolCalls),
    errors
  };
};

export function AudioChatAdapter(action: ChatEvent): ChatMessageUpdate {

  const noItemId = () => ({
    type: 'error',
    error: {
      type: 'adapter_internal_error',
      code: 'missing_item_id',
      message: `missing an item id for the ${action.type}`
    },
    sourceObj: action
  } as ErrorItem);

  switch (action.type) {

    case "conversation.item.create":
    case "conversation.item.created":
    case "response.output_item.added":

      if (!action.item.id) return noItemId();

      const meta: AKMeta = {
        time: Date.now(),
        version: 0,
        hidden: action.item.type !== 'message' || action.item.role === 'system'
      };

      switch (action.item.type) {
        case 'message':
          return {
            id: action.item.id,
            type: 'message',
            content: action.item?.content?.map(c => ({ type: 'text', content: c.text! })) ?? [],
            role: action.item.role || 'user',
            meta
          };
        case 'function_call':
          return {
            id: action.item.id,
            type: 'tool_call',
            arguments: action.item.arguments || '',
            callId: action.item.call_id || '[no call_id]',
            name: action.item.name || '[no function name]',
            meta
          }
        case 'function_call_output':
          return {
            id: action.item.id,
            type: 'tool_call_output',
            callId: action.item.call_id || '[no call_id]',
            output: action.item.output || '[no output]',
            meta
          }
        default:
          return {
            type: 'error',
            error: {
              type: 'unsupported_item_type',
              message: `unsupported type found: ${action.item.type}`
            },
            sourceObj: action
          };
      }

    case "input_audio_buffer.speech_started":
      return {
        id: action.item_id,
        type: 'message',
        content: [],
        role: 'user',
        meta: {
          time: Date.now(),
          version: 0,
          phase: "in_process"
        }
      };

    case "input_audio_buffer.speech_stopped":
      return {
        id: action.item_id,
        type: 'update_meta',
        meta: {
          time: Date.now(),
          phase: "pending",
          version: 1
        }
      };

    // remote added a new response
    case "conversation.item.input_audio_transcription.delta":
    case "response.audio_transcript.delta":
    case "response.text.delta": {
      return {
        id: action.item_id,
        type: 'content_delta',
        index: action.content_index ?? 0,
        content: { type: 'text', content: action.delta || '' },
        sourceObj: action
      }
    }
    case "response.output_item.done":
      if (!action.item.id) return noItemId();
      // remote has completed the response
      return {
        id: action.item.id,
        type: 'update_meta',
        meta: {
          time: Date.now(),
          phase: "done"
        }
      };
    case "conversation.item.input_audio_transcription.completed":
      return {
        id: action.item_id,
        type: 'update_meta',
        meta: {
          time: Date.now(),
          phase: "done"
        }
      };
    case "response.text.done":
    case "response.audio_transcript.done": {
      let completedText = (action as ResponseTextDoneEvent).text;
      let completedTranscript = (action as ResponseAudioTranscriptDoneEvent).transcript;
      return {
        id: action.item_id,
        type: 'content_delta',
        index: action.content_index,
        overwrite: true,
        content: { type: 'text', content: completedText ?? completedTranscript },
        meta: {
          time: Date.now(),
          phase: 'done'
        }
      }
    }
    case "error":
      return {
        type: 'error',
        error: {
          code: action.error.code,
          type: action.type,
          message: action.error.message
        }
      }
    case "response.function_call_arguments.done": {
      return {
        id: action.item_id,
        type: 'tool_call',
        arguments: action.arguments,
        meta: {
          time: Date.now(),
          hidden: true
        }
      };
    }
    case "update-item-meta":
      return {
        type: 'update_meta',
        id: action.id,
        meta: action.update as AKMeta
      }
    default:
      return {
        type: 'ignore',
        sourceObj: action
      }
  }
};


